import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  FilterOptionsState,
  ListItem,
  ListItemText,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { isString } from '@vy/utils';
import React, { useCallback } from 'react';
import { Size, SIZES } from '../../types/sizes';

type VyAutocompleteSelectProps<OptionType> = {
  label?: string;
  options: OptionType[];
  onOptionSelect?: (selectedOption: OptionType | null | string) => void;
  onInputChange?: (inputValue: string) => void;
  filterOption?: (option: OptionType, searchQuery: string) => boolean;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  getOptionLabel?: (option: OptionType) => string;
  fullWidth?: boolean;
  value?: OptionType;
  size?: Extract<Size, typeof SIZES.SMALL | typeof SIZES.MEDIUM>;
  disabled?: boolean;
  optionsKeyExtractor?: (option: OptionType) => React.Key;
  allowCustomOptions?: boolean;
  disableClearable?: boolean;
  sx?: SxProps<Theme>;
};

const labelSx: SxProps<Theme> = {
  fontSize: '12px',
  marginBottom: '8px',
  color: '#1B3D5599',
};

const defaultFilterOption = (option: unknown, searchQuery: string): boolean =>
  String(option).toLowerCase().includes(searchQuery.toLowerCase());

const defaultGetOptionLabel = (option: unknown): string => String(option);

export const VyAutocompleteSelect = <OptionType,>({
  label = 'Select',
  options,
  placeholder = '',
  onOptionSelect,
  filterOption = defaultFilterOption,
  error,
  helperText,
  getOptionLabel = defaultGetOptionLabel,
  fullWidth,
  value,
  size = SIZES.MEDIUM,
  disabled,
  optionsKeyExtractor,
  allowCustomOptions,
  disableClearable,
  onInputChange,
  sx,
}: VyAutocompleteSelectProps<OptionType>) => {
  const handleFilterOptions = useCallback(
    (options: OptionType[], state: FilterOptionsState<OptionType>) =>
      options.filter((option) => filterOption(option, state.inputValue)),
    [filterOption],
  );

  const handleChange = useCallback(
    (_: unknown, newValue: OptionType | string | null) =>
      isString(newValue) ? undefined : onOptionSelect?.(newValue),
    [onOptionSelect],
  );

  const handleInputChange = useCallback(
    (_: unknown, newInputValue: string) => {
      onInputChange?.(newInputValue);
    },
    [onInputChange],
  );

  const getOptionLabelMemo = useCallback(
    (option: OptionType | string) => (isString(option) ? option : getOptionLabel(option)),
    [getOptionLabel],
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        error={error}
        placeholder={placeholder}
        helperText={helperText}
        fullWidth={fullWidth}
        size={size}
      />
    ),
    [error, helperText, placeholder, fullWidth, size],
  );

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: OptionType) => (
      <ListItem {...props} key={optionsKeyExtractor?.(option)}>
        <ListItemText>{getOptionLabel(option)}</ListItemText>
      </ListItem>
    ),
    [getOptionLabel, optionsKeyExtractor],
  );

  return (
    <Box>
      {label && <Typography sx={labelSx}>{label}</Typography>}
      <Autocomplete
        value={value ?? null}
        options={options}
        disabled={disabled}
        fullWidth={fullWidth}
        disableClearable={disableClearable}
        getOptionLabel={getOptionLabelMemo}
        sx={sx}
        filterOptions={handleFilterOptions}
        onChange={handleChange}
        freeSolo={allowCustomOptions}
        onInputChange={handleInputChange}
        renderInput={renderInput}
        renderOption={renderOption}
      />
    </Box>
  );
};
